import { RouteConfig } from 'config/constants/route'
import { UIBreakPointConfig, UIComponentConfig } from 'config/layout'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import React from 'react'
import styled from 'styled-components'
import { Icons } from 'svgs'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import Grid from 'UIKit/Box/Grid'
import { BoxProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image'
import { NextLinkFromReactRouter } from 'UIKit/NextLink'
import { SearchButtonIcon, SearchInput } from './AppSearch'
import BalanceSelect from './BalanceSelect'
import ConnectionGroupButton from './ConnectionGroupButton'
import LanguageSelectTopbar from './Menu/component/LanguageMenu/LanguageSelectTopbar'
import { NotificationButton } from './Notification'
import { RowCenter } from './Row'
import UserMenu from './UserMenu'

const StyledContainer = styled(Box)`
  height: ${({ theme }) => theme.topbarHeight};
  width: 100%;
`

const StyledWrapper = styled(Grid)`
  background: ${({ theme }) => theme.colors.background} !important;

  height: 100%;
  width: 100%;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};

  ${({ theme }) => theme.mediaQueries.sm} {
    background: linear-gradient(
      180deg,
      rgba(22, 25, 31, 0.8) 9.3%,
      rgba(22, 25, 31, 0.608) 41.64%,
      rgba(22, 25, 31, 0.448) 69.77%,
      rgba(22, 25, 31, 0) 99.79%
    );

    border-bottom: unset;
  }

  & > * {
    transition: ${({ theme }) => theme.transitions.fast};
  }
`

const StyledSearchIcon = styled(SearchButtonIcon)`
  ${({ theme }) => theme.mediaQueries.lg} {
    display: none;
  }
`

const StyledUserMenu = styled(UserMenu)`
  display: none;

  ${({ theme }) => theme.mediaQueries.md} {
    display: flex;
  }
`

const StyledLanguageSelectTopbar = styled(LanguageSelectTopbar)`
  display: none;

  ${({ theme }) => theme.mediaQueries.md} {
    display: flex;
  }
`

export const TopbarContainer: React.FC<BoxProps> = ({ children, ...props }) => {
  return <StyledContainer {...props}>{children}</StyledContainer>
}

const Topbar: React.FC<BoxProps & { logoDisplayConfig?: UIBreakPointConfig<UIComponentConfig> }> = ({
  logoDisplayConfig = {
    desktop: {
      hidden: true,
    },
    tablet: { hidden: true },
  },
  ...props
}) => {
  return (
    <TopbarContainer
      id="app-topbar"
      position={['fixed', 'fixed', 'relative']}
      {...props}
      ml={['0px', '0px', '0px', '24px']}
    >
      <StyledWrapper
        gridTemplateColumns={[
          logoDisplayConfig?.mobile?.hidden ? '1fr auto auto' : 'auto 1fr auto auto',
          ,
          logoDisplayConfig?.tablet?.hidden ? '1fr auto 1fr' : 'auto 1fr auto 1fr',
          ,
          logoDisplayConfig?.desktop?.hidden ? '1fr auto 1fr' : 'auto 1fr auto 1fr',
        ]}
        pl={['0px', '12px', '12px', '16px']}
        pr={['0px', '12px', , '40px']}
      >
        <Box
          width={['44px', , , '134.7px']}
          mr={['0px', , , '24px']}
          as={NextLinkFromReactRouter}
          to={RouteConfig.Home}
          id="logo-hunny-small"
          display={[
            logoDisplayConfig?.mobile?.hidden ? 'none' : 'block',
            ,
            logoDisplayConfig?.tablet?.hidden ? 'none' : 'block',
            ,
            logoDisplayConfig?.desktop?.hidden ? 'none' : 'block',
          ]}
        >
          <RowCenter height="100%" display={['flex !important', , , 'none !important']}>
            <Image src="/logo-hunny-200.png" height={32} width={32} alt="logo" />
          </RowCenter>

          <RowCenter height="100%" display={['none !important', , , 'flex !important']}>
            <Image src="/logo-text.png" height={40} width={134.7} alt="logo" />
          </RowCenter>
        </Box>
        <TopbarContent />
      </StyledWrapper>
    </TopbarContainer>
  )
}
const TopbarContent = React.memo(() => {
  const { isMobile } = useMatchBreakpoints()

  return isMobile ? (
    <>
      <Flex justifyContent="center" ml={['6px', '12px']} mr="12px" flex={1}>
        <BalanceSelect
          style={{
            width: '100%',
          }}
        />
        <ConnectionGroupButton />
      </Flex>
      <StyledSearchIcon
        SearchIcon={Icons.SearchIconAlt}
        width="24px !important"
        height="24px !important"
        mx="7px !important"
      />
    </>
  ) : (
    <>
      <SearchInput />
      <StyledSearchIcon ml={['0px', '', '', '"12px"']} />

      <BalanceSelect ml="12px" />

      <Flex alignItems="center" justifyContent="flex-end">
        <ConnectionGroupButton mr="12px" />

        <StyledUserMenu id="topbar-usermenu" ml="8px" />

        <NotificationButton ml="8px" />

        <StyledLanguageSelectTopbar id="topbar-languageselect" ml="8px" />
      </Flex>
    </>
  )
})

export default Topbar
